import { useState } from 'react';
import { useQueries, useQuery } from '@tanstack/react-query';
import { Badge, Button, Dialog, EmptyState, Field, Input, Section, Select, Spinner, Switch } from '@zt/ui';
import { api } from '../lib/api';
import { HelpTip, PageFrame } from '../lib/help';
import { useI18n, useT } from '../lib/i18n';
import { fieldError, useApiMutation } from '../lib/mutation';
import { usePermissions } from '../lib/session';
import type { DestructionLog, RetentionPreview, RetentionRuleRow, RetentionView } from '../lib/types';

/**
 * Saklama ve imha (F6-19, ADR-101): otomatik imha kuralları (varsayılan kapalı; açmak geri alınamaz silme
 * onayı ve iki adımlı doğrulama ister), özelliklerin sabit saklama süreleri ve imha kaydı.
 */
export function RetentionPage() {
  const t = useT();
  const { can } = usePermissions();
  const q = useQuery({
    queryKey: ['retention'],
    queryFn: () => api.get<RetentionView>('/v1/admin/privacy/retention'),
  });
  return (
    <PageFrame helpKey="page.retention">
      {!q.data ? (
        <Spinner label={t('common.loading')} />
      ) : (
        <>
          <RulesForm
            key={JSON.stringify(q.data.rules)}
            saved={q.data}
            manage={can('privacy.retention.manage')}
          />
          <FixedSection data={q.data} />
        </>
      )}
      <LogSection />
    </PageFrame>
  );
}

type FormState = Record<string, { enabled: boolean; days: string }>;

function RulesForm({ saved, manage }: { saved: RetentionView; manage: boolean }) {
  const t = useT();
  const { formatNumber, formatDate } = useI18n();
  const [form, setForm] = useState<FormState>(() =>
    Object.fromEntries(saved.rules.map((r) => [r.key, { enabled: r.enabled, days: String(r.days) }])),
  );
  const [confirming, setConfirming] = useState<RetentionRuleRow[] | null>(null);
  const body = (confirm: boolean) => ({
    rules: Object.fromEntries(
      saved.rules.map((r) => [r.key, { enabled: form[r.key]!.enabled, days: Number(form[r.key]!.days) }]),
    ),
    ...(confirm ? { confirmDestruction: true } : {}),
  });
  const save = useApiMutation(
    (confirm: boolean) => api.put<RetentionView>('/v1/admin/privacy/retention', body(confirm)),
    {
      invalidate: [['retention']],
      success: t('admin.retention.saved'),
      silentFieldErrors: true,
      onSuccess: () => setConfirming(null),
    },
  );
  const enabling = saved.rules.filter((r) => !r.enabled && form[r.key]!.enabled);
  const set = (key: string, patch: Partial<FormState[string]>) =>
    setForm((f) => ({ ...f, [key]: { ...f[key]!, ...patch } }));
  return (
    <Section
      title={t('admin.retention.rulesTitle')}
      description={t('admin.retention.rulesLead')}
      action={<HelpTip k="retention.rules" />}
    >
      <div className="overflow-x-auto rounded-md border border-line">
        <table className="w-full text-sm" aria-label={t('admin.retention.rulesTitle')}>
          <thead className="bg-surface-2 text-ink-2">
            <tr>
              <th className="px-3 py-2 text-start font-medium">{t('admin.retention.colData')}</th>
              <th className="px-3 py-2 text-start font-medium">
                <span className="inline-flex items-center gap-1">
                  {t('admin.retention.colDays')} <HelpTip k="retention.days" />
                </span>
              </th>
              <th className="px-3 py-2 text-end font-medium">
                <span className="inline-flex items-center gap-1">
                  {t('admin.retention.colDue')} <HelpTip k="retention.due" />
                </span>
              </th>
              <th className="px-3 py-2 text-start font-medium">
                <span className="inline-flex items-center gap-1">
                  {t('admin.retention.colEnabled')} <HelpTip k="retention.enabled" />
                </span>
              </th>
            </tr>
          </thead>
          <tbody>
            {saved.rules.map((r) => {
              const name = t(`admin.retention.rules.${r.key}`);
              const err = fieldError(save.error, `rules.${r.key}.days`);
              return (
                <tr key={r.key} className="border-t border-line align-top">
                  <td className="px-3 py-3">
                    <span className="font-medium">{name}</span>
                    <span className="mt-0.5 block max-w-[40ch] text-ink-3">
                      {t(`admin.retention.ruleLeads.${r.key}`)}
                    </span>
                  </td>
                  <td className="px-3 py-3">
                    <Input
                      type="number"
                      inputMode="numeric"
                      className="w-28"
                      min={r.minDays}
                      max={r.maxDays}
                      value={form[r.key]!.days}
                      disabled={!manage}
                      aria-label={t('admin.retention.daysLabel', { name })}
                      aria-invalid={err ? true : undefined}
                      aria-describedby={`ret-${r.key}-range`}
                      onChange={(e) => set(r.key, { days: e.target.value })}
                    />
                    <span id={`ret-${r.key}-range`} className="mt-1 block text-xs text-ink-3">
                      {t('admin.retention.daysRange', { min: r.minDays, max: r.maxDays, def: r.defaultDays })}
                    </span>
                    {err && (
                      <span role="alert" className="mt-1 block text-xs text-rose">
                        {err}
                      </span>
                    )}
                  </td>
                  <td className="px-3 py-3 text-end tabular-nums">
                    {t('admin.retention.dueValue', { n: formatNumber(r.due) })}
                  </td>
                  <td className="px-3 py-3">
                    <span className="flex items-center gap-2">
                      <Switch
                        checked={form[r.key]!.enabled}
                        disabled={!manage}
                        label={t('admin.retention.enabledLabel', { name })}
                        onCheckedChange={(v) => set(r.key, { enabled: v })}
                      />
                      {r.enabled ? (
                        <Badge tone="moss">{t('admin.retention.on')}</Badge>
                      ) : (
                        <Badge tone="slate">{t('admin.retention.off')}</Badge>
                      )}
                    </span>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
      <p className="mt-3 text-sm text-ink-3">
        {saved.lastDestructionDay
          ? t('admin.retention.lastRun', { day: formatDate(saved.lastDestructionDay, false) })
          : t('admin.retention.noRunYet')}
      </p>
      {manage && (
        <div className="mt-4 flex items-center gap-1">
          <Button
            variant="primary"
            loading={save.isPending && !confirming}
            onClick={() => (enabling.length ? setConfirming(enabling) : save.mutate(false))}
          >
            {t('common.save')}
          </Button>
          <HelpTip k="retention.save" />
        </div>
      )}
      {confirming && (
        <ConfirmDialog
          rules={confirming}
          days={(k) => Number(form[k]!.days)}
          busy={save.isPending}
          error={save.error && !save.error.errors.length ? save.error.title : undefined}
          onClose={() => setConfirming(null)}
          onConfirm={() => save.mutate(true)}
        />
      )}
    </Section>
  );
}

function ConfirmDialog({
  rules,
  days,
  busy,
  error,
  onClose,
  onConfirm,
}: {
  rules: RetentionRuleRow[];
  days: (key: string) => number;
  busy: boolean;
  error?: string;
  onClose: () => void;
  onConfirm: () => void;
}) {
  const t = useT();
  const { formatNumber } = useI18n();
  const [ack, setAck] = useState(false);
  const previews = useQueries({
    queries: rules.map((r) => ({
      queryKey: ['retention-preview', r.key, days(r.key)],
      queryFn: () =>
        api.get<RetentionPreview>(`/v1/admin/privacy/retention/preview?rule=${r.key}&days=${days(r.key)}`),
      retry: false,
    })),
  });
  return (
    <Dialog
      open
      onOpenChange={(v) => !v && onClose()}
      title={t('admin.retention.confirmTitle')}
      description={t('admin.retention.confirmLead')}
      closeLabel={t('common.close')}
      footer={
        <>
          <Button onClick={onClose}>{t('common.cancel')}</Button>
          <Button variant="danger" loading={busy} disabled={!ack} onClick={onConfirm}>
            {t('admin.retention.confirmButton')}
          </Button>
          <HelpTip k="retention.confirm" />
        </>
      }
    >
      <ul className="space-y-2 text-sm">
        {rules.map((r, i) => {
          const p = previews[i];
          return (
            <li key={r.key} className="rounded-md border border-line px-3 py-2">
              <span className="font-medium">{t(`admin.retention.rules.${r.key}`)}</span>{' '}
              <span className="text-ink-2">
                {p?.data
                  ? t('admin.retention.confirmRow', { days: days(r.key), n: formatNumber(p.data.due) })
                  : p?.error
                    ? t('admin.retention.confirmRowUnknown', { days: days(r.key) })
                    : t('common.loading')}
              </span>
            </li>
          );
        })}
      </ul>
      <label className="mt-4 flex items-start gap-2 text-sm">
        <input
          type="checkbox"
          className="mt-1 accent-[var(--zt-pine)]"
          checked={ack}
          onChange={(e) => setAck(e.target.checked)}
        />
        <span>{t('admin.retention.confirmAck')}</span>
      </label>
      {error && (
        <p role="alert" className="mt-3 text-sm text-rose">
          {error}
        </p>
      )}
    </Dialog>
  );
}

function FixedSection({ data }: { data: RetentionView }) {
  const t = useT();
  return (
    <Section
      title={t('admin.retention.fixedTitle')}
      description={t('admin.retention.fixedLead')}
      action={<HelpTip k="retention.fixed" />}
    >
      <div className="overflow-x-auto rounded-md border border-line">
        <table className="w-full text-sm" aria-label={t('admin.retention.fixedTitle')}>
          <thead className="bg-surface-2 text-ink-2">
            <tr>
              <th className="px-3 py-2 text-start font-medium">{t('admin.retention.colData')}</th>
              <th className="px-3 py-2 text-end font-medium">{t('admin.retention.colRetention')}</th>
            </tr>
          </thead>
          <tbody>
            {data.fixed.map((f) => (
              <tr key={f.key} className="border-t border-line">
                <td className="px-3 py-2">{t(`admin.retention.rules.${f.key}`)}</td>
                <td className="px-3 py-2 text-end tabular-nums">
                  {t('admin.retention.days', { n: f.days })}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </Section>
  );
}

const LOG_DAYS = ['30', '90', '365', '1095'] as const;

function LogSection() {
  const t = useT();
  const { formatNumber, formatDate } = useI18n();
  const [days, setDays] = useState<(typeof LOG_DAYS)[number]>('90');
  const q = useQuery({
    queryKey: ['destruction-log', days],
    queryFn: () => api.get<DestructionLog>(`/v1/admin/privacy/destruction-log?days=${days}`),
  });
  return (
    <Section
      title={t('admin.retention.logTitle')}
      description={t('admin.retention.logLead')}
      action={<HelpTip k="retention.log" />}
    >
      <div className="mb-4 max-w-xs">
        <Field label={t('admin.retention.period')} help={<HelpTip k="retention.log_period" />}>
          {(p) => (
            <Select {...p} value={days} onChange={(e) => setDays(e.target.value as typeof days)}>
              {LOG_DAYS.map((d) => (
                <option key={d} value={d}>
                  {t('admin.retention.lastDays', { n: Number(d) })}
                </option>
              ))}
            </Select>
          )}
        </Field>
      </div>
      {!q.data ? (
        <Spinner label={t('common.loading')} />
      ) : q.data.rows.length === 0 ? (
        <EmptyState title={t('admin.retention.logEmpty')} description={t('admin.retention.logEmptyLead')} />
      ) : (
        <div className="overflow-x-auto rounded-md border border-line">
          <table className="w-full text-sm" aria-label={t('admin.retention.logTitle')}>
            <thead className="bg-surface-2 text-ink-2">
              <tr>
                <th className="px-3 py-2 text-start font-medium">{t('admin.retention.colDay')}</th>
                <th className="px-3 py-2 text-start font-medium">{t('admin.retention.colRule')}</th>
                <th className="px-3 py-2 text-start font-medium">{t('admin.retention.colMethod')}</th>
                <th className="px-3 py-2 text-end font-medium">{t('admin.retention.colCount')}</th>
                <th className="px-3 py-2 text-end font-medium">{t('admin.retention.colRetention')}</th>
              </tr>
            </thead>
            <tbody>
              {q.data.rows.map((r) => (
                <tr key={`${r.day}-${r.rule}-${r.method}`} className="border-t border-line">
                  <td className="px-3 py-2 tabular-nums">{formatDate(r.day, false)}</td>
                  <td className="px-3 py-2">{t(`admin.retention.rules.${r.rule}`)}</td>
                  <td className="px-3 py-2">{t(`admin.retention.methods.${r.method}`)}</td>
                  <td className="px-3 py-2 text-end tabular-nums">{formatNumber(r.count)}</td>
                  <td className="px-3 py-2 text-end tabular-nums">
                    {r.retentionDays === null
                      ? t('admin.retention.byRequest')
                      : t('admin.retention.days', { n: r.retentionDays })}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </Section>
  );
}
